<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { asyncComputed } from '@vueuse/core'
import { useTranslation } from "i18next-vue";

import type { ChangeCallbackParams } from "@/types/config";
import { SettingTypes, CfgGroups } from "@/types/enums";

import { configStore } from "@/stores/config";
import { addErrorBottom } from "@/stores/messages";

import UCToggle from "@/components/ui/UCToggle.vue";
import UCRange from '@/components/ui/UCRange.vue';

const { t } = useTranslation();

const wakeupSensitivityValue = ref<null | number>(0);
const displayTimeoutValue = ref<null | number>(0);
const sleepTimeoutValue = ref<null | number>(0);
const config = configStore();

const wakeOnWlan = ref<boolean>(false);

const settingsSection = ref();

config.$subscribe((mutation, state) => {
  setConfigValues();
});

const wakeupSensitivityLevels = ref([
  t('settings.power_saving.wakeup_sensitivity.level.off'),
  t('settings.power_saving.wakeup_sensitivity.level.low'),
  t('settings.power_saving.wakeup_sensitivity.level.medium'),
  t('settings.power_saving.wakeup_sensitivity.level.high'),
]);

const wowlanAvailable = asyncComputed(async() => {
  if (import.meta.env.VITE_WOWLAN === "true") {
    return true;
  }

  let model = "";
  try {
    model = await config.getDeviceModel();
  } catch (e: any) {
    addErrorBottom(e);
  }
  return model?.toLowerCase() == "ucr2";
});

const displayBrightnessSetting = computed(() => {
  return {
    name: "wakeup_sensitivity",
    value: wakeupSensitivityValue.value,
    label: t('settings.power_saving.wakeup_sensitivity.label'),
    type: '',
    group: CfgGroups.power_saving,
    settings: {
      min: 0,
      max: 3,
      step: 1,
      showLimits: true,
    }
  }
});

const displayTimeoutSetting = computed(() => {
  return {
    name: "display_off_sec",
    value: displayTimeoutValue.value,
    label: t('settings.power_saving.display_timeout.label'),
    type: SettingTypes.SECONDS,
    group: CfgGroups.power_saving,
    settings: {
      min: 10,
      max: 60,
      showValue: true,
      showLimits: true,
      valueFormatter: null,
    }
  }
});

const sleepTimeoutSetting = computed(() => {
  return {
    name: "standby_sec",
    value: sleepTimeoutValue.value,
    label: t('settings.power_saving.sleep_timeout.label'),
    type: SettingTypes.SECONDS,
    group: CfgGroups.power_saving,
    settings: {
      min: 10,
      max: 1800,
      showValue: true,
      showLimits: true,
      valueFormatter: null,
    }
  }
});

function setConfigValues() {
  wakeOnWlan.value = config.$state.config?.network?.wake_on_wlan?.enabled || false;
  wakeupSensitivityValue.value = config.$state.config?.power_saving?.wakeup_sensitivity ? config.$state.config?.power_saving?.wakeup_sensitivity : 0;
  sleepTimeoutValue.value = config.$state.config?.power_saving?.standby_sec || 0;
  displayTimeoutValue.value = config.$state.config?.power_saving?.display_off_sec || 0;
}

async function wakeOnWlanChanged() {
  const message = {
    wake_on_wlan: {
      enabled: wakeOnWlan.value
    }
  }

  try {
    await config.baseUpdate(CfgGroups.network as string, message);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

async function wakeupSensitivityChanged(params: ChangeCallbackParams) {
  onItemChange(params);
}

async function displayTimeoutChanged(params: ChangeCallbackParams) {
  onItemChange(params);
}

async function sleepTimeoutChanged(params: ChangeCallbackParams) {
  onItemChange(params);
}

async function onItemChange(params: ChangeCallbackParams) {
  try {
    await config.update(params.group as string, params.name as string, params.value);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

onMounted(() => {
  setConfigValues()
})

</script>
<template>
  <div class="page-settings-section" ref="settingsSection">
    <h1 class="page-settings-section__title">
      {{ $t('settings.power_saving.name') }}
    </h1>
    <div class="page-settings-section__main">
      <UCToggle
        v-if="wowlanAvailable"
        v-model="wakeOnWlan"
        @change="wakeOnWlanChanged"
        :label="$t('settings.power_saving.wake_on_wlan.label')"
        :full-w="true"
        :settings="true"
        :description="$t('settings.power_saving.wake_on_wlan.description')"
      />
      <UCRange
        v-model="wakeupSensitivityValue"
        :change-callback="wakeupSensitivityChanged"
        :key="'wakeup-sensitivity-value'"
        :definition="displayBrightnessSetting"
        :name="displayBrightnessSetting.name"
        :value="wakeupSensitivityValue!"
        :points="[0, 1, 2, 3]"
        :pointsLabel="wakeupSensitivityLevels"
        :label="$t('settings.power_saving.wakeup_sensitivity.label')"
        :description="$t('settings.power_saving.wakeup_sensitivity.description')"
        :settings="true"
      />
      <UCRange
        v-model="displayTimeoutValue"
        :change-callback="displayTimeoutChanged"
        :key="'display-timeout-value'"
        :definition="displayTimeoutSetting"
        :name="displayTimeoutSetting.name"
        :value="displayTimeoutValue!"
        :label="$t('settings.power_saving.display_timeout.label')"
        :description="$t('settings.power_saving.display_timeout.description')"
        :settings="true"
      />
      <UCRange
        v-model="sleepTimeoutValue"
        :change-callback="sleepTimeoutChanged"
        :key="'sleep-timeout-value'"
        :definition="sleepTimeoutSetting"
        :name="sleepTimeoutSetting.name"
        :value="sleepTimeoutValue!"
        :label="$t('settings.power_saving.sleep_timeout.label')"
        :description="$t('settings.power_saving.sleep_timeout.description')"
        :settings="true"
      />
    </div>
  </div>
</template>